import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AppShell, Button, type AdaptedNavItem } from '@command-center/ui-system';
import {
  RightSidebarProvider,
  SharedRightSidebar,
  useRightSidebar,
} from '@command-center/oneup-portal-ui';
import { useAuth, SignIn } from './auth';
import { apiBase } from './auth';
import { exitBrowserImpersonation } from '@command-center/auth-sdk/browser-impersonation';
import { AlicePanel } from './AlicePanel';
import { CommandPalette, type PaletteContext } from './components/CommandPalette';
import { NewProjectFlow } from './components/NewProjectFlow';
import { SidebarCommandButton, WorkspaceMenu } from './components/ShellControls';
import { ClientsView } from './views/ClientsView';
import { ContactsView } from './views/ContactsView';
import { DocumentsView } from './views/DocumentsView';
import { ProjectsView } from './views/ProjectsView';
import { ApprovalsView, fetchPendingApprovalDocuments } from './views/ApprovalsView';
import { BriefDialog, ProjectWorkspace } from './views/ProjectWorkspace';
import {
  ActivityView,
  AliceFrameworkView,
  ProfileView,
  WorkspaceSettingsView,
} from './views/WorkspacePages';
import { ecorpShellConfig, getEcorpRouteHeader } from './shell.config';

export function App() {
  return (
    <RightSidebarProvider storageKey="ecorp:right-sidebar">
      <AppContent />
    </RightSidebarProvider>
  );
}

function AppContent() {
  const auth = useAuth();
  const rightSidebar = useRightSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [alicePrompt, setAlicePrompt] = useState<string>();
  const [context, setContext] = useState<Record<string, unknown>>({});
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [newProject, setNewProject] = useState<{ open: boolean; mode?: 'manual' | 'siteplan' }>({
    open: false,
  });
  const [briefProjectId, setBriefProjectId] = useState<string>();
  const [approvalCount, setApprovalCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const pending = await fetchPendingApprovalDocuments();
        if (!cancelled) setApprovalCount(pending.length);
      } catch {
        // Keep the last known count when a background refresh is unavailable.
      }
    }
    void poll();
    const timer = window.setInterval(() => void poll(), 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const decorateNav = useCallback(
    (item: AdaptedNavItem) => {
      if (item.id !== 'approvals' || approvalCount <= 0) return undefined;
      return { badge: approvalCount, badgeTone: 'default' as const };
    },
    [approvalCount],
  );

  useEffect(() => {
    const segment = location.pathname.split('/')[1] || 'projects';
    if (location.pathname.startsWith('/projects/')) {
      setContext((current) => ({
        ...current,
        route: location.pathname,
        page: 'project-workspace',
        resource: segment,
      }));
    } else {
      setContext((current) => {
        const {
          projectId: _projectId,
          projectName: _projectName,
          pendingSuggestions: _pending,
          documentId: _documentId,
          documentName: _documentName,
          ...rest
        } = current;
        return { ...rest, route: location.pathname, page: segment, resource: segment };
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((current) => !current);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const openNewProject = useCallback((mode?: 'manual' | 'siteplan') => {
    setNewProject({ open: true, mode });
  }, []);
  const openAlice = useCallback((prompt?: string) => {
    setAlicePrompt(prompt);
    rightSidebar.showPanel('alice');
  }, [rightSidebar.showPanel]);
  const openUpload = useCallback(
    (projectId?: string) => {
      const currentProjectId =
        typeof context.projectId === 'string' ? context.projectId : undefined;
      const target = projectId ?? currentProjectId;
      if (target && location.pathname === `/projects/${target}`) {
        window.dispatchEvent(new CustomEvent('ecorp:open-upload'));
      } else if (target) {
        navigate(`/projects/${target}`);
        window.setTimeout(() => window.dispatchEvent(new CustomEvent('ecorp:open-upload')), 400);
      } else {
        openNewProject('siteplan');
      }
    },
    [context.projectId, location.pathname, navigate, openNewProject],
  );

  const paletteContext = useMemo<PaletteContext>(
    () => ({
      projectId: typeof context.projectId === 'string' ? context.projectId : undefined,
      projectName: typeof context.projectName === 'string' ? context.projectName : undefined,
      documentId: typeof context.documentId === 'string' ? context.documentId : undefined,
      openNewProject,
      openUpload,
      openAlice,
      openBrief: (projectId) => setBriefProjectId(projectId),
      openMissingInfo: (projectId) => {
        navigate(`/projects/${projectId}`);
        openAlice('What information is still missing for this project?');
      },
    }),
    [
      context.documentId,
      context.projectId,
      context.projectName,
      navigate,
      openAlice,
      openNewProject,
      openUpload,
    ],
  );

  if (auth.loading) {
    return (
      <div className="grid min-h-screen place-items-center text-muted-foreground">
        Checking session…
      </div>
    );
  }
  if (!auth.session && !auth.impersonation) return <SignIn />;
  if (auth.access === 'checking') {
    return (
      <div className="grid min-h-screen place-items-center text-muted-foreground">
        Checking ECORP access…
      </div>
    );
  }
  if (auth.access === 'denied') {
    return (
      <div className="grid min-h-screen place-items-center p-6">
        <div className="max-w-lg rounded-xl border bg-card p-8 text-center">
          <h1 className="text-xl font-semibold">Access unavailable</h1>
          <p className="mt-2 text-muted-foreground">{auth.error}</p>
          <Button className="mt-5" variant="outline" onClick={() => void auth.signOut()}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  const mergeContext = (value: Record<string, unknown>) => {
    setContext((currentContext) => ({ ...currentContext, ...value }));
  };
  const userName =
    auth.actor?.name ??
    String(auth.user?.user_metadata?.full_name ?? auth.user?.user_metadata?.name ?? '');
  const userEmail = auth.actor?.email ?? auth.user?.email;

  return (
    <>
    {auth.impersonation && (
      <div className="sticky top-0 z-[100] flex flex-wrap items-center justify-between gap-2 border-b border-amber-400/50 bg-amber-100 px-4 py-2 text-sm text-amber-950">
        <strong>
          Viewing as {auth.impersonation.impersonation.targetName} for {auth.impersonation.impersonation.organizationName} — Read Only
        </strong>
        <span>
          {auth.impersonation.impersonation.portalLabel} · expires {new Date(auth.impersonation.impersonation.expiresAt).toLocaleTimeString()}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void exitBrowserImpersonation(
            apiBase,
            `https://admin.oneuphi.com/users/${encodeURIComponent(auth.impersonation!.impersonation.targetAppUserId)}`,
          )}
        >
          Exit Impersonation
        </Button>
      </div>
    )}
    <AppShell
      branding={ecorpShellConfig.branding}
      groups={ecorpShellConfig.navigation}
      decorate={decorateNav}
      head={getEcorpRouteHeader(location.pathname)}
      headerVariant="workspace"
      collapsibleNavGroups
      navGroupStorageKey="ecorp-nav"
      collapsibleSidebar
      sidebarStorageKey={`${ecorpShellConfig.appId}:sidebar`}
      sidebarCommand={({ compact }) => (
        <SidebarCommandButton compact={compact} onOpen={() => setPaletteOpen(true)} />
      )}
      sidebarWorkspace={({ compact }) => (
        <WorkspaceMenu
          compact={compact}
          organizationName={ecorpShellConfig.organization.name ?? 'ECORP'}
          userName={userName || undefined}
          userEmail={userEmail}
          onSignOut={() => void auth.signOut()}
        />
      )}
      sidebarFooter={<span>{ecorpShellConfig.footer}</span>}
      contentClassName="px-4 pt-4 sm:px-5 sm:pt-5 lg:px-6 lg:pt-6"
      mobileRightSidebar
      rightSidebarOpenToken={rightSidebar.openToken}
      rightSidebarLabel="Alice, Record Details, and Messages"
      rightSidebar={
        <SharedRightSidebar
          aliceEnabled={Boolean(ecorpShellConfig.assistant?.enabled)}
          alice={({ collapse }) => (
            <AlicePanel
              onCollapse={collapse}
              context={context}
              initialPrompt={alicePrompt}
              onPromptConsumed={() => setAlicePrompt(undefined)}
              launchers={{
                openNewProject,
                openUpload,
                openBrief: (projectId) => setBriefProjectId(projectId),
                openMissingInfo: (projectId) => paletteContext.openMissingInfo(projectId),
              }}
            />
          )}
        />
      }
      overlays={
        <>
          <CommandPalette
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
            context={paletteContext}
          />
          <NewProjectFlow
            open={newProject.open}
            initialMode={newProject.mode}
            onClose={() => setNewProject({ open: false })}
            onCreated={(projectId) => navigate(`/projects/${projectId}`)}
          />
          <BriefDialog
            open={Boolean(briefProjectId)}
            onClose={() => setBriefProjectId(undefined)}
            projectId={briefProjectId}
          />
        </>
      }
    >
      <Routes>
        <Route path="/" element={<Navigate to="/projects" replace />} />
        <Route
          path="/projects"
          element={<ProjectsView onContext={mergeContext} onNewProject={openNewProject} />}
        />
        <Route path="/projects/:id" element={<ProjectWorkspace onContext={mergeContext} />} />
        <Route path="/clients" element={<ClientsView onContext={mergeContext} />} />
        <Route path="/clients/:id" element={<ClientsView onContext={mergeContext} />} />
        <Route path="/contacts" element={<ContactsView onContext={mergeContext} />} />
        <Route path="/contacts/:id" element={<ContactsView onContext={mergeContext} />} />
        <Route path="/documents" element={<DocumentsView onContext={mergeContext} />} />
        <Route path="/documents/:id" element={<DocumentsView onContext={mergeContext} />} />
        <Route
          path="/approvals"
          element={<ApprovalsView onContext={mergeContext} onCountChange={setApprovalCount} />}
        />
        <Route path="/activity" element={<ActivityView onContext={mergeContext} />} />
        <Route path="/alice-framework" element={<AliceFrameworkView />} />
        <Route path="/settings" element={<WorkspaceSettingsView />} />
        <Route path="/profile" element={<ProfileView />} />
        <Route path="*" element={<Navigate to="/projects" replace />} />
      </Routes>
    </AppShell>
    </>
  );
}
